import { Avatar, Box, Card, Heading, Icon, Image, Tag, Text } from '@zillow/constellation-10';
import { IconHomeTypeFilled } from '@zillow/constellation-icons';
import { TourStatusForZcheduler } from '@zg-rentals/ts-z-calendar-client';

import { getTourStatusTone } from '../../utils/tourStatusDisplay';

type EventDetailsProps = {
  bookedByName?: string;
  eventDateAndTime: string;
  listingStreet: string;
  photoUrl?: string;
  title: string;
  tourStatus?: TourStatusForZcheduler;
  /** When set, the renter row links to that lead's details. Omitted for non-tour events (availability, reschedule). */
  leadHref?: string;
};

const EventDetails = ({
  bookedByName,
  eventDateAndTime,
  listingStreet,
  photoUrl,
  title,
  tourStatus,
  leadHref,
}: EventDetailsProps) => {
  const isCancelledTour = tourStatus === TourStatusForZcheduler.CANCELLED;

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }} data-testid="EventDetails">
      <Box css={{ display: 'flex', flexDirection: 'row', gap: 'default' }}>
        {photoUrl ? (
          <Image
            alt={`Photo of ${listingStreet}`}
            css={{ borderRadius: '12px', objectFit: 'cover', overflow: 'hidden', height: '72px', width: '72px' }}
            src={photoUrl}
          />
        ) : (
          <Icon css={{ borderRadius: '12px', height: '72px', width: '72px' }}>
            <IconHomeTypeFilled />
          </Icon>
        )}
        <Box>
          <Tag tone={getTourStatusTone(tourStatus)} css={{ marginBottom: 'tightest' }} size="sm">
            {title}
          </Tag>
          <Heading
            css={isCancelledTour ? { textDecoration: 'line-through' } : undefined}
            data-strikethrough={isCancelledTour || undefined}
            data-testid="EventDetails-dateTime"
            level={6}
          >
            {eventDateAndTime}
          </Heading>
          <Text textStyle="body-sm">{listingStreet}</Text>
        </Box>
      </Box>
      {bookedByName &&
        (leadHref ? (
          <Card.Root interactive elevated={false}>
            <Card.BlockAnchor
              href={leadHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View ${bookedByName}'s lead details`}
              data-testid="EventDetails-renterLink"
            />
            <Card.Content>
              <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'tighter' }}>
                <Avatar fullName={bookedByName} size="xs" />
                <Text>{bookedByName}</Text>
              </Box>
            </Card.Content>
          </Card.Root>
        ) : (
          <Card elevated={false}>
            <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'tighter' }}>
              <Avatar fullName={bookedByName} size="xs" />
              <Text>{bookedByName}</Text>
            </Box>
          </Card>
        ))}
    </Box>
  );
};

export default EventDetails;
